import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPlus, FaUsers } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` });

const EMPTY_FORM = { title: "", subject: "", topic: "", scheduled_at: "", external_link: "" };

const TeacherLiveSessions = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [attendees, setAttendees] = useState({});
    const [recordingDrafts, setRecordingDrafts] = useState({});

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/live-sessions/mine`, { headers: authHeaders() });
            const data = await res.json();
            setSessions(data.data || []);
        } catch (err) {
            toast.error("Could not load your sessions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSessions(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title || !form.subject || !form.scheduled_at || !form.external_link) {
            toast.error("Please fill in every field");
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/live-sessions`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to schedule session");
            toast.success("Session scheduled");
            setForm(EMPTY_FORM);
            setShowForm(false);
            fetchSessions();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const loadAttendees = async (id) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/live-sessions/${id}/attendees`, { headers: authHeaders() });
            const data = await res.json();
            setAttendees((prev) => ({ ...prev, [id]: data.data }));
        } catch (err) {
            // silent
        }
    };

    const saveRecording = async (id) => {
        const url = recordingDrafts[id];
        if (!url) return;
        try {
            await fetch(`${API_BASE_URL}/api/live-sessions/${id}`, {
                method: "PATCH",
                headers: authHeaders(),
                body: JSON.stringify({ recording_url: url }),
            });
            toast.success("Recording link added");
            fetchSessions();
        } catch (err) {
            toast.error("Failed to save recording link");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                    <h1 className="text-xl font-bold text-white">Live Sessions</h1>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-white text-blue600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
                    <FaPlus /> Schedule
                </button>
            </nav>

            <div className="max-w-3xl mx-auto p-6">
                {showForm && (
                    <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm p-6 space-y-4 mb-6">
                        <input required placeholder="Title" className="border rounded-lg p-2 w-full" value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        <div className="grid grid-cols-2 gap-4">
                            <input required placeholder="Subject" className="border rounded-lg p-2" value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                            <input placeholder="Topic (optional)" className="border rounded-lg p-2" value={form.topic}
                                onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                        </div>
                        <input required type="datetime-local" className="border rounded-lg p-2 w-full" value={form.scheduled_at}
                            onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                        <input required placeholder="Meeting link (Zoom, Google Meet, etc.)" className="border rounded-lg p-2 w-full" value={form.external_link}
                            onChange={(e) => setForm({ ...form, external_link: e.target.value })} />
                        <button disabled={saving} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                            {saving ? "Saving..." : "Schedule Session"}
                        </button>
                    </form>
                )}

                {loading && <p className="text-gray-500">Loading...</p>}
                {!loading && sessions.length === 0 && <p className="text-gray-500">No sessions scheduled yet.</p>}

                <div className="space-y-3">
                    {sessions.map((s) => (
                        <div key={s.id} className="bg-white rounded-xl shadow-sm p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-semibold text-gray-800">{s.title}</h3>
                                    <p className="text-sm text-gray-500">{s.subject}{s.topic ? ` · ${s.topic}` : ""}</p>
                                    <p className="text-xs text-gray-400 mt-1">{new Date(s.scheduled_at).toLocaleString()}</p>
                                </div>
                                <button onClick={() => loadAttendees(s.id)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg" title="Attendees">
                                    <FaUsers /> {s.attendeeCount}
                                </button>
                            </div>
                            {attendees[s.id] && (
                                <div className="text-xs text-gray-600 mt-2 border-t pt-2">
                                    {attendees[s.id].length === 0 ? "No one registered yet." :
                                        attendees[s.id].map((a) => `${a.firstName} ${a.lastName}`).join(", ")}
                                </div>
                            )}
                            {!s.recording_url ? (
                                <div className="flex gap-2 mt-3">
                                    <input placeholder="Paste recording link after the session"
                                        className="border rounded-lg p-2 flex-1 text-sm"
                                        value={recordingDrafts[s.id] || ""}
                                        onChange={(e) => setRecordingDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))} />
                                    <button onClick={() => saveRecording(s.id)} className="bg-gray-100 px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700">
                                        Save
                                    </button>
                                </div>
                            ) : (
                                <p className="text-xs text-green-600 mt-2">Recording added ✓</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TeacherLiveSessions;
